import React, { useContext, useEffect, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IModGroupParentItem } from 'types';
import { updateModGroupProductsInclusive } from 'backend/modifierGroups';
import { ModalContent } from 'components/ModalWindows/ModalContent';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import Text from 'components/Text/Text';
import { InclusiveProductItem } from './InclusiveProductItem';
import style from './index.module.scss';

interface IInclusiveProduct {
	productId: string;
	name: string;
	externalId: string;
	inclusive: number;
	isEnabled: boolean;
}

interface IProps {
	onCloseModal: () => void;
	onFailureEvent?: () => void;
	onShowError: () => void;
	restaurantId: string;
	modifierGroupId: string;
	modifierGroupName: string;
	customerName: string;
	restaurantName: string;
	parents: IModGroupParentItem[];
}

export const ModalEditInclusiveForModGroup: React.FC<IProps> = ({
	onCloseModal,
	onFailureEvent,
	onShowError,
	restaurantId,
	modifierGroupId,
	modifierGroupName,
	customerName,
	restaurantName,
	parents,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [products, setProducts] = useState<IInclusiveProduct[]>([]);

	useEffect(() => {
		if (parents) {
			const initialProducts = parents.map((parent) => ({
				productId: parent.id,
				name: parent.name,
				externalId: parent.externalId,
				inclusive: parent.inclusive || 0,
				isEnabled: parent.inclusive > 0,
			}));
			setProducts(initialProducts);
		}
	}, [parents]);

	const handleToggleInclusive = (productId: string, isEnabled: boolean) => {
		setProducts((prev) =>
			prev.map((product) =>
				product.productId === productId
					? { ...product, isEnabled, inclusive: isEnabled ? product.inclusive : 0 }
					: product,
			),
		);
	};

	const handleChangeInclusiveValue = (productId: string, value: number) => {
		setProducts((prev) =>
			prev.map((product) => (product.productId === productId ? { ...product, inclusive: value } : product)),
		);
	};

	const isDisabledSave = products.some((product) => product.isEnabled && !product.inclusive);

	const handleSave = async () => {
		try {
			showLoader();
			const productsToUpdate = products.map((product) => ({
				productId: product.productId,
				inclusive: product.isEnabled ? product.inclusive : 0,
			}));
			await updateModGroupProductsInclusive({
				restaurantId,
				modifierGroupId,
				products: productsToUpdate,
			});
			onCloseModal();
		} catch (err) {
			onShowError();
		} finally {
			hideLoader();
		}
	};

	return (
		<ModalContent
			onConsentEvent={handleSave}
			onFailureEvent={onFailureEvent ? onFailureEvent : onCloseModal}
			heading="editInclusiveHeading"
			headerTranslationNameSpace="modals"
			translationOfConfirmBtn="saveButton"
			headingAlignment="left"
			centered={false}
			isDisabledSave={isDisabledSave}>
			<Text translationKey="editInclusiveDescription" translationNameSpace="modals" size="s" />
			<div className={style.row}>
				<div>
					<FullInfoField label="customerLabel" />
					<Text translationKey={customerName} size="s" withTooltip={true} />
				</div>
				<div>
					<FullInfoField label="restaurantLabel" />
					<Text translationKey={restaurantName} size="s" withTooltip={true} />
				</div>
				<div>
					<FullInfoField label="modifierGroupLabel" />
					<Text translationKey={modifierGroupName} size="s" withTooltip={true} />
				</div>
			</div>
			<div className={style.productsList}>
				{products.map((product) => (
					<InclusiveProductItem
						key={product.productId}
						productId={product.productId}
						name={product.name}
						inclusive={product.inclusive}
						isEnabled={product.isEnabled}
						onToggle={handleToggleInclusive}
						onChangeValue={handleChangeInclusiveValue}
					/>
				))}
			</div>
		</ModalContent>
	);
};
